import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import ListingCard from "@/components/ListingCard";
import { Loader2, ArrowRight, PackageSearch, ChevronLeft } from "lucide-react";

// Deterministic brand monogram (same treatment as the Feed brand rail — no copyrighted logos).
function brandColor(name) {
  let h = 0;
  for (let i = 0; i < String(name).length; i++) h = (h * 31 + String(name).charCodeAt(i)) % 360;
  return `hsl(${h} 42% 30%)`;
}
function brandInitials(name) {
  const words = String(name).replace(/[^A-Za-z0-9 &]/g, " ").trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return String(name).replace(/[^A-Za-z0-9]/g, "").slice(0, 2).toUpperCase() || "?";
}

export default function BrandPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(false);
    api.get(`/brands/${slug}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  const brand = data?.brand;
  const count = data?.listing_count || 0;
  const listings = data?.listings || [];

  return (
    <div className="os-page min-h-screen text-foreground" data-testid="brand-page">
      <Navbar />
      <div className="max-w-[1280px] mx-auto px-4 py-8">
        <Link to="/feed" data-testid="brand-back-feed" className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wide text-muted-foreground hover:text-olive-bright transition-colors mb-5">
          <ChevronLeft className="w-3.5 h-3.5" /> Marketplace
        </Link>

        {loading ? (
          <div className="flex items-center gap-2 text-secondary-foreground font-mono text-sm py-16" data-testid="brand-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading brand…
          </div>
        ) : error || !brand ? (
          <div className="border border-sniper bg-surface p-10 flex flex-col items-center text-center gap-3" data-testid="brand-not-found">
            <PackageSearch className="w-9 h-9 text-muted-foreground" strokeWidth={1.5} />
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">Brand not found</h1>
            <p className="font-body text-sm text-muted-foreground max-w-md">We couldn&apos;t find a brand page for &ldquo;{slug}&rdquo;. It may not have any active listings right now.</p>
            <Link to="/feed" className="mt-1 inline-flex items-center gap-1.5 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm px-5 py-2.5 transition-colors">
              Browse the marketplace <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <>
            {/* Hero */}
            <div className="relative border border-sniper bg-surface overflow-hidden" data-testid="brand-hero">
              <div className="absolute inset-0 opacity-[0.14]" aria-hidden style={{ background: `radial-gradient(circle at 15% 20%, ${brandColor(brand)} 0%, transparent 55%)` }} />
              <div className="relative flex flex-col sm:flex-row sm:items-center gap-5 p-6 sm:p-8">
                <span
                  className="inline-flex items-center justify-center w-20 h-20 shrink-0 rounded-md font-heading font-bold text-2xl text-white/95 border border-white/10"
                  style={{ backgroundColor: brandColor(brand) }}
                  data-testid="brand-hero-mark"
                  aria-hidden
                >
                  {brandInitials(brand)}
                </span>
                <div className="min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-olive-bright mb-1">OpticSwap Brand</p>
                  <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight leading-none" data-testid="brand-hero-name">{brand}</h1>
                  <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mt-2" data-testid="brand-hero-count">
                    {count} active listing{count === 1 ? "" : "s"}
                  </p>
                </div>
                <Link
                  to={`/feed?brand=${encodeURIComponent(brand)}`}
                  data-testid="brand-shop-cta"
                  className="sm:ml-auto shrink-0 inline-flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm px-5 py-3 transition-colors"
                >
                  Filter marketplace <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Listings */}
            <h2 className="font-heading text-lg font-bold uppercase tracking-tight mt-8 mb-3" data-testid="brand-listings-heading">
              {brand} listings
            </h2>
            {listings.length === 0 ? (
              <div className="border border-sniper bg-surface p-10 flex flex-col items-center text-center gap-2" data-testid="brand-empty">
                <PackageSearch className="w-8 h-8 text-muted-foreground" strokeWidth={1.5} />
                <p className="font-heading text-lg uppercase tracking-tight">No active listings yet</p>
                <p className="font-body text-sm text-muted-foreground">No one has {brand} gear up for sale right now — check back soon.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="brand-listings-grid">
                {listings.map((l, i) => <ListingCard key={l.listing_id} listing={l} index={i} />)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
